<template>
    <form v-on:submit.prevent="saveForm" novalidate>
        <XIcon
            class="
                h-8
                w-8
                text-blue-900
                hover:text-gray-400
                absolute
                cursor-pointer
                right-0
                top-0
                m-4
                p-4
                -mt-2
                mr-3
            "
            @click="closeModal()"
        />

        <section :class="{ 'opacity-30': form.delete }">
            <label class="block text-sm font-medium text-gray-700 -mt-1">{{
                t("form-user.name", "Naam")
            }}</label>
            <div class="mt-1 relative rounded-md shadow-sm">
                <input
                    type="text"
                    class="
                        block
                        w-full
                        pr-10
                        focus:outline-none
                        sm:text-sm
                        rounded-md
                    "
                    :class="{
                        'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500':
                            getError('name'),
                    }"
                    @keypress="errors = null"
                    v-model="form.name"
                />
                <div
                    class="
                        absolute
                        inset-y-0
                        right-0
                        pr-3
                        flex
                        items-center
                        pointer-events-none
                    "
                >
                    <ExclamationCircleIcon
                        v-if="getError('name')"
                        class="h-5 w-5 text-red-500"
                        aria-hidden="true"
                    />
                </div>
            </div>
            <p class="mt-2 text-xs text-red-600" v-if="getError('name')">
                {{ getError("name", true) }}
            </p>

            <!-- EMAIL -->
            <label class="block text-sm font-medium text-gray-700 mt-2">{{
                t("form-user.email", "E-mailadres")
            }}</label>
            <div class="mt-1 relative rounded-md shadow-sm">
                <input
                    type="email"
                    class="
                        block
                        w-full
                        pr-10
                        focus:outline-none
                        sm:text-sm
                        rounded-md
                    "
                    :class="{
                        'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500':
                            getError('email'),
                        'cursor-not-allowed':
                            form && form.id && !getError('email'),
                    }"
                    no-keydown="errors = null"
                    v-model="form.email"
                    :disabled="form.id && !getError('email')"
                    :readonly="form.id && !getError('email')"
                />
                <div
                    class="
                        absolute
                        inset-y-0
                        right-0
                        pr-3
                        flex
                        items-center
                        pointer-events-none
                    "
                >
                    <ExclamationCircleIcon
                        v-if="getError('email')"
                        class="h-5 w-5 text-red-500"
                        aria-hidden="true"
                    />
                </div>
            </div>
            <p class="mt-2 text-xs text-red-600" v-if="getError('email')">
                {{ getError("email", true) }}
            </p>

            <!-- PHONE -->
            <label class="block text-sm font-medium text-gray-700 mt-2">{{
                t("form-user.phone", "Telefoonnummer")
            }}</label>
            <div class="mt-1 relative rounded-md shadow-sm">
                <input
                    type="tel"
                    class="
                        block
                        w-full
                        pr-10
                        focus:outline-none
                        sm:text-sm
                        rounded-md
                    "
                    :class="{
                        'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500':
                            getError('phone'),
                    }"
                    @keypress="errors = null"
                    v-model="form.phone"
                />
                <div
                    class="
                        absolute
                        inset-y-0
                        right-0
                        pr-3
                        flex
                        items-center
                        pointer-events-none
                    "
                >
                    <ExclamationCircleIcon
                        v-if="getError('phone')"
                        class="h-5 w-5 text-red-500"
                        aria-hidden="true"
                    />
                </div>
            </div>
            <p class="mt-2 text-xs text-red-600" v-if="getError('phone')">
                {{ getError("phone", true) }}
            </p>

            <!-- PHONE -->
            <label class="block text-sm font-medium text-gray-700 mt-2">{{
                t("form-user.voting-weight", "Stemgewicht")
            }}</label>
            <div class="mt-1 relative rounded-md shadow-sm">
                <input
                    type="number"
                    min="0"
                    step="1"
                    class="
                        block
                        w-full
                        pr-10
                        focus:outline-none
                        sm:text-sm
                        rounded-md
                    "
                    :class="{
                        'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500':
                            getError('voting_weight'),
                    }"
                    @keypress="errors = null"
                    v-model="form.voting_weight"
                />
                <div
                    class="
                        absolute
                        inset-y-0
                        right-0
                        pr-3
                        flex
                        items-center
                        pointer-events-none
                    "
                >
                    <ExclamationCircleIcon
                        v-if="getError('voting_weight')"
                        class="h-5 w-5 text-red-500"
                        aria-hidden="true"
                    />
                </div>
            </div>
            <p
                class="mt-2 text-xs text-red-600"
                v-if="getError('voting_weight')"
            >
                {{ getError("voting_weight", true) }}
            </p>

            <!-- TAGS -->
            <label class="block text-sm font-medium text-gray-700 mt-2">{{
                t("form-user.tags", "Tags")
            }}</label>
            <div class="mt-1">
                <div class="flex flex-wrap gap-1 mb-2" v-if="form.tags && form.tags.length > 0">
                    <span
                        v-for="(tag, index) in form.tags"
                        :key="index"
                        class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                    >
                        {{ tag }}
                        <button
                            type="button"
                            class="ml-1 inline-flex items-center justify-center w-4 h-4 text-blue-400 hover:text-blue-600"
                            @click="removeTag(index)"
                        >
                            &times;
                        </button>
                    </span>
                </div>
                <div class="relative rounded-md shadow-sm">
                    <input
                        type="text"
                        class="block w-full pr-10 focus:outline-none sm:text-sm rounded-md focus:ring-blue-500 focus:border-blue-500 border-gray-300"
                        :placeholder="t('form-user.tags-placeholder', 'Typ een tag en druk op Enter')"
                        v-model="tagInput"
                        @keydown.enter.prevent="addTag"
                        @keydown.,="addTag"
                    />
                </div>
            </div>

            <SwitchGroup as="div" class="flex items-center mt-4">
                <Switch
                    v-model="form.is_moderator"
                    @click="
                        form.is_moderator
                            ? (form.can_publish = true)
                            : (form.can_publish = form.can_publish)
                    "
                    :class="[
                        form.is_moderator ? 'bg-blue-600' : 'bg-gray-200',
                        'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500',
                    ]"
                >
                    <span
                        :class="[
                            form.is_moderator
                                ? 'translate-x-5'
                                : 'translate-x-0',
                            'pointer-events-none relative inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200',
                        ]"
                    >
                        <span
                            :class="[
                                form.is_moderator
                                    ? 'opacity-0 ease-out duration-100'
                                    : 'opacity-100 ease-in duration-200',
                                'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity',
                            ]"
                            aria-hidden="true"
                        >
                            <svg
                                class="h-3 w-3 text-gray-400"
                                fill="none"
                                viewBox="0 0 12 12"
                            >
                                <path
                                    d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                        </span>
                        <span
                            :class="[
                                form.is_moderator
                                    ? 'opacity-100 ease-in duration-200'
                                    : 'opacity-0 ease-out duration-100',
                                'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity',
                            ]"
                            aria-hidden="true"
                        >
                            <svg
                                class="h-3 w-3 text-blue-600"
                                fill="currentColor"
                                viewBox="0 0 12 12"
                            >
                                <path
                                    d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z"
                                />
                            </svg>
                        </span>
                    </span>
                </Switch>

                <SwitchLabel as="span" class="ml-3">
                    <span class="text-sm font-medium text-gray-900">{{
                        t("form-user.moderator-rights", "Moderator-rechten")
                    }}</span>
                    <!-- <span class="text-sm text-gray-500">(Save 10%)</span> -->
                </SwitchLabel>
            </SwitchGroup>

            <SwitchGroup
                as="div"
                class="flex items-center mt-4"
                :class="{
                    'opacity-70 disabled pointer-events-none':
                        form.is_moderator,
                }"
            >
                <Switch
                    v-model="form.can_publish"
                    class="
                        relative
                        inline-flex
                        flex-shrink-0
                        h-6
                        w-11
                        border-2 border-transparent
                        rounded-full
                        cursor-pointer
                        transition-colors
                        ease-in-out
                        duration-200
                        focus:outline-none
                        focus:ring-2
                        focus:ring-offset-2
                        focus:ring-blue-500
                    "
                    :class="{
                        'bg-blue-600': form.can_publish,
                        'bg-gray-200': !form.can_publish,
                        'opacity-70 disabled pointer-events-none':
                            form.is_moderator,
                    }"
                >
                    <div
                        :class="{
                            'opacity-70 disabled pointer-events-none':
                                form.is_moderator,
                        }"
                    >
                        <span
                            :class="[
                                form.can_publish
                                    ? 'translate-x-5'
                                    : 'translate-x-0',
                                'pointer-events-none relative inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200',
                            ]"
                        >
                            <span
                                :class="[
                                    form.can_publish
                                        ? 'opacity-0 ease-out duration-100'
                                        : 'opacity-100 ease-in duration-200',
                                    'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity',
                                ]"
                                aria-hidden="true"
                            >
                                <svg
                                    class="h-3 w-3 text-gray-400"
                                    fill="none"
                                    viewBox="0 0 12 12"
                                >
                                    <path
                                        d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                                        stroke="currentColor"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                </svg>
                            </span>
                            <span
                                :class="[
                                    form.can_publish
                                        ? 'opacity-100 ease-in duration-200'
                                        : 'opacity-0 ease-out duration-100',
                                    'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity',
                                ]"
                                aria-hidden="true"
                            >
                                <svg
                                    class="h-3 w-3 text-blue-600"
                                    fill="currentColor"
                                    viewBox="0 0 12 12"
                                >
                                    <path
                                        d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z"
                                    />
                                </svg>
                            </span>
                        </span>
                    </div>
                </Switch>

                <SwitchLabel
                    as="span"
                    class="ml-3"
                    :class="{
                        'opacity-70 disabled pointer-events-none':
                            form.is_moderator,
                    }"
                >
                    <span
                        class="text-sm font-medium text-gray-900"
                        :class="{
                            'opacity-70 disabled pointer-events-none':
                                form.is_moderator,
                        }"
                        >{{
                            t("form-user.publish-rights", "Uitzend-rechten")
                        }}</span
                    >
                    <!-- <span class="text-sm text-gray-500">(kan deelnemen met de webcam tijdens vergadering)</span> -->
                </SwitchLabel>
            </SwitchGroup>

            <SwitchGroup
                as="div"
                class="flex items-center mt-3"
                v-show="edit.id"
            >
                <Switch
                    v-model="form.is_blocked"
                    :class="[
                        form.is_blocked ? 'bg-red-600' : 'bg-gray-200',
                        'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500',
                    ]"
                >
                    <span
                        :class="[
                            form.is_blocked ? 'translate-x-5' : 'translate-x-0',
                            'pointer-events-none relative inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200',
                        ]"
                    >
                        <span
                            :class="[
                                form.is_blocked
                                    ? 'opacity-0 ease-out duration-100'
                                    : 'opacity-100 ease-in duration-200',
                                'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity',
                            ]"
                            aria-hidden="true"
                        >
                            <svg
                                class="h-3 w-3 text-gray-400"
                                fill="none"
                                viewBox="0 0 12 12"
                            >
                                <path
                                    d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                        </span>
                        <span
                            :class="[
                                form.is_blocked
                                    ? 'opacity-100 ease-in duration-200'
                                    : 'opacity-0 ease-out duration-100',
                                'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity',
                            ]"
                            aria-hidden="true"
                        >
                            <svg
                                class="h-3 w-3 text-red-600"
                                fill="currentColor"
                                viewBox="0 0 12 12"
                            >
                                <path
                                    d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z"
                                />
                            </svg>
                        </span>
                    </span>
                </Switch>

                <SwitchLabel as="span" class="ml-3">
                    <span class="text-sm font-medium text-gray-900">{{
                        t("form-user.revoke-access", "Toegang ontzeggen")
                    }}</span>
                    <!-- <span class="text-xs text-gray-500" v-if="form.is_blocked"
                    >(Deelnemer kan niet meer inloggen)</span
                > -->
                </SwitchLabel>
            </SwitchGroup>

            <SwitchGroup
                as="div"
                class="flex items-center mt-3"
                v-show="edit.id && !form.is_blocked"
            >
                <Switch
                    v-model="form.has_pin"
                    :class="[
                        form.has_pin ? 'bg-blue-600' : 'bg-gray-200',
                        'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500',
                    ]"
                >
                    <span
                        :class="[
                            form.has_pin ? 'translate-x-5' : 'translate-x-0',
                            'pointer-events-none relative inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200',
                        ]"
                    >
                        <span
                            :class="[
                                form.has_pin
                                    ? 'opacity-0 ease-out duration-100'
                                    : 'opacity-100 ease-in duration-200',
                                'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity',
                            ]"
                            aria-hidden="true"
                        >
                            <svg
                                class="h-3 w-3 text-gray-400"
                                fill="none"
                                viewBox="0 0 12 12"
                            >
                                <path
                                    d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                        </span>
                        <span
                            :class="[
                                form.has_pin
                                    ? 'opacity-100 ease-in duration-200'
                                    : 'opacity-0 ease-out duration-100',
                                'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity',
                            ]"
                            aria-hidden="true"
                        >
                            <svg
                                class="h-3 w-3 text-blue-600"
                                fill="currentColor"
                                viewBox="0 0 12 12"
                            >
                                <path
                                    d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z"
                                />
                            </svg>
                        </span>
                    </span>
                </Switch>

                <SwitchLabel as="span" class="ml-3">
                    <span class="text-sm font-medium text-gray-900">{{
                        t("form-user.add-access-code", "Toegangscode koppelen")
                    }}</span>
                    <!-- <span class="text-sm text-gray-500">(Save 10%)</span> -->
                </SwitchLabel>
            </SwitchGroup>

            <!-- PHONE -->
            <section v-show="form.has_pin && !form.is_blocked">
                <label class="block text-sm font-medium text-gray-700 mt-2">{{
                    t("form-user.access-code", "Toegangscode")
                }}</label>
                <div class="mt-1 relative rounded-md shadow-sm">
                    <input
                        type="text"
                        class="
                            block
                            w-full
                            pr-10
                            focus:outline-none
                            sm:text-sm
                            rounded-md
                            cursor-not-allowed
                        "
                        readonly
                        disabled
                        v-model="form.pincode"
                    />
                </div>
            </section>

            <SwitchGroup
                as="div"
                class="flex items-center mt-3"
                v-show="edit.id && form.chat_blocked"
            >
                <Switch
                    v-model="form.can_chat"
                    :class="[
                        form.can_chat ? 'bg-gray-600' : 'bg-gray-200',
                        'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500',
                    ]"
                >
                    <span
                        :class="[
                            form.can_chat ? 'translate-x-5' : 'translate-x-0',
                            'pointer-events-none relative inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200',
                        ]"
                    >
                        <span
                            :class="[
                                form.can_chat
                                    ? 'opacity-0 ease-out duration-100'
                                    : 'opacity-100 ease-in duration-200',
                                'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity',
                            ]"
                            aria-hidden="true"
                        >
                            <svg
                                class="h-3 w-3 text-gray-400"
                                fill="none"
                                viewBox="0 0 12 12"
                            >
                                <path
                                    d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                        </span>
                        <span
                            :class="[
                                form.can_chat
                                    ? 'opacity-100 ease-in duration-200'
                                    : 'opacity-0 ease-out duration-100',
                                'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity',
                            ]"
                            aria-hidden="true"
                        >
                            <svg
                                class="h-3 w-3 text-gray-600"
                                fill="currentColor"
                                viewBox="0 0 12 12"
                            >
                                <path
                                    d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z"
                                />
                            </svg>
                        </span>
                    </span>
                </Switch>

                <SwitchLabel as="span" class="ml-3">
                    <span class="text-sm font-medium text-gray-900">{{
                        t("form-user.can_chat", "Mag chatten")
                    }}</span>
                    <!-- <span class="text-xs text-gray-500" v-if="form.is_blocked"
                    >(Deelnemer kan niet meer inloggen)</span
                > -->
                </SwitchLabel>
            </SwitchGroup>
        </section>
        <SwitchGroup
            as="div"
            class="flex items-center mt-3"
            v-show="edit.id"
            @click="checkDelete()"
        >
            <Switch
                v-model="form.delete"
                :class="[
                    form.delete ? 'bg-red-600' : 'bg-gray-200',
                    'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500',
                ]"
            >
                <span
                    :class="[
                        form.delete ? 'translate-x-5' : 'translate-x-0',
                        'pointer-events-none relative inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200',
                    ]"
                >
                    <span
                        :class="[
                            form.delete
                                ? 'opacity-0 ease-out duration-100'
                                : 'opacity-100 ease-in duration-200',
                            'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity',
                        ]"
                        aria-hidden="true"
                    >
                        <svg
                            class="h-3 w-3 text-gray-400"
                            fill="none"
                            viewBox="0 0 12 12"
                        >
                            <path
                                d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                    </span>
                    <span
                        :class="[
                            form.delete
                                ? 'opacity-100 ease-in duration-200'
                                : 'opacity-0 ease-out duration-100',
                            'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity',
                        ]"
                        aria-hidden="true"
                    >
                        <svg
                            class="h-3 w-3 text-red-600"
                            fill="currentColor"
                            viewBox="0 0 12 12"
                        >
                            <path
                                d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z"
                            />
                        </svg>
                    </span>
                </span>
            </Switch>

            <SwitchLabel as="span" class="ml-3">
                <span class="text-sm font-medium text-gray-900">{{
                    t("form-user.delete-user", "Verwijder deelnemer")
                }}</span>
                <span class="text-xs text-red-700 block" v-if="form.delete">{{
                    t(
                        "form-user.delete-confirm",
                        "Kan niet ongedaan gemaakt worden"
                    )
                }}</span>
            </SwitchLabel>
        </SwitchGroup>

        <div class="mt-3 sm:mt-6">
            <button
                type="submit"
                class="
                    inline-flex
                    justify-center
                    w-full
                    rounded-md
                    border border-transparent
                    shadow-sm
                    px-4
                    py-2
                    text-base
                    font-medium
                    focus:outline-none
                    focus:ring-2
                    focus:ring-offset-2
                    focus:ring-blue-500
                    sm:text-sm
                    disabled:opacity-50
                "
                :class="{
                    'bg-red-600 text-red-50 hover:bg-red-700': form.delete,
                    'bg-blue-600 text-blue-50 hover:bg-blue-700': !form.delete,
                }"
                :disabled="loading"
            >
                <svg
                    v-if="loading"
                    class="animate-spin h-5 w-5 mr-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                >
                    <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                    ></circle>
                    <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                </svg>
                {{ t("save", "Opslaan") }}
            </button>
        </div>
    </form>
</template>

<script>
import { ExclamationCircleIcon, XIcon, TrashIcon } from "@heroicons/vue/solid";
import { Switch, SwitchGroup, SwitchLabel } from "@headlessui/vue";
export default {
    components: {
        ExclamationCircleIcon,
        Switch,
        SwitchGroup,
        SwitchLabel,
        XIcon,
        TrashIcon,
    },
    mounted() {
        if (this.edit) {
            this.form = this.edit;
            if (!this.form.tags) this.form.tags = [];
        }
        Eventing.fire("formUser");
    },
    created() {
        Eventing.fire("show-upload");
    },
    props: ["edit"],
    data() {
        return {
            form: {
                email: null,
                name: null,
                tags: [],
            },
            tagInput: "",
            loading: false,
            errors: null,
        };
    },
    methods: {
        closeModal() {
            Eventing.fire("closeModal");
        },
        addTag() {
            const val = this.tagInput.replace(/,/g, "").trim();
            if (val && !this.form.tags.includes(val)) {
                if (!this.form.tags) this.form.tags = [];
                this.form.tags.push(val);
            }
            this.tagInput = "";
        },
        removeTag(index) {
            this.form.tags.splice(index, 1);
        },
        getError(error, text) {
            try {
                if (this.errors) {
                    let errors = Object.values(this.errors);
                    if (text) {
                        return this.errors[error].toString();
                    }
                    return this.errors[error].toString() ? true : false;
                }
            } catch (error) {
                return false;
            }
            return false;
        },
        saveForm() {
            let self = this;
            this.loading = true;
            var url = "/api/users";
            var method = "post";
            if (this.edit.hash && this.edit.hash != "undefined") {
                url = "/api/users/" + this.edit.hash;
                method = "put";
            }
            axios({
                method: method,
                url: url,
                data: this.form,
            })
                .then((response) => {
                    this.loading = false;
                    Eventing.fire("fetchUsers");
                    if (response.data.data.is_blocked) {
                        this.$router.push({ name: "attendees-inactive" });
                    } else {
                        this.$router.push({ name: "attendees-index" });
                    }
                })
                .catch((error) => {
                    if (error.response.status == 404) {
                        this.$router.push("/login");
                    } else if (error.response.status == 422) {
                        this.errors = error.response.data.errors;
                        // this.texts.content = errors["pincode"].toString();
                    } else if (error.response.status == 409) {
                        // this.texts.content = error.response.data.error;
                    } else {
                        this.closeModal();
                        // alert("Daar ging wat mis");
                    }
                    this.loading = false;
                });
        },
        checkDelete() {
            var val = this.form.delete;
            if (val == true) {
                Eventing.fire("warning");
            } else {
                Eventing.fire("noWarning");
            }
        },
    },
    watch: {},
};
</script>
